import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * Hard card-at-signup gate (platform policy = 'hard'). A trial tenant with no payment method is routed to
 * Billing and can't use the rest of the app until a card is added. When the platform policy is
 * 'skippable' this is a no-op (the dashboard BillingNudge handles the gentle reminder instead). The
 * Billing pages themselves are always reachable so the tenant can actually add the card.
 */
export default function BillingGate({ children }) {
  const loc = useLocation();
  const navigate = useNavigate();
  const { can } = useAuth();
  const canConfigureBilling = can("billing:configure");

  useEffect(() => {
    // A hard card policy is an owner/billing-admin workflow. Applying it to a teacher or a
    // read-only accountant would both disclose subscription metadata and trap them on a page where
    // they cannot add the required payment method.
    if (!canConfigureBilling) return undefined;
    if (loc.pathname.startsWith("/app/billing")) return;   // never gate the billing surface itself
    let alive = true;
    api.get("/billing/subscription").then(({ data }) => {
      if (!alive) return;
      const s = data.subscription || {};
      const policy = data.platform_billing?.card_policy;
      if (policy === "hard" && s.status === "trial" && !s.payment_method) {
        navigate("/app/billing?welcome=1", { replace: true });
      }
    }).catch(() => {});
    return () => { alive = false; };
  }, [canConfigureBilling, loc.pathname, navigate]);

  return children;
}
